import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Tooltip, Icon, message, Switch, Button, InputNumber, Typography, Spin, Upload, Modal, Select } from 'antd';
import { useTranslation, withTranslation } from 'react-i18next';
import { unzipSync, strFromU8 } from 'fflate';
import DocumentName from '../../components/DocumentName';
import AutoSaveButton from './AutoSaveButton';
import {
  useDocumentContext,
  useComplexDocumentHistory,
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
} from '../../context/complexDocumentContext';
import { useArtboardContext, CONTEXT_KEYS } from '../../context/complexArtboardContext';
import { ActionButton, UndoIcon, RedoIcon, EndContainer, PreviewEndContainer, PreviewPageNumberInput, DownloadButton } from './styles';
import { DownloadButton as ImportButton } from './MenuBar/styles';
import { TemplateName } from '../SidebarMenu/styles';
import { COMPLETE_TRANSACTION } from './translationValues';
import { COMPLEX_DESIGN_TOOLBAR } from './translationValues';
import {
  updateResourceNames,
  lockResource,
  unlockResource,
  checkLockStatus,
  updateCommunicationName,
  fetchStandaloneBlock
} from '../../utils/services/complexDocumentsAPI';
import { getAllImages } from '../../utils/services/imageAPI';
import { COMPLEX_DOCUMENT_PAGE_WRAPPER } from '../../utils/complexDocumentConstants';
import ComplexDocumentVersion from './ComplexDocumentVersion';
import { useSessionContext } from '../../context/sessionContext';
import { partialMatchKey } from '../../utils/importHelper';
import { ENTERPRISE_ENV, checkUserAccessRoles, extractUnsupportedCharacters } from '../../utils/helper';
import { IMPORT_REUSABLE_PREFIX, constructDesignFromInstructions } from './complexDocumentHelper';
import { getAllCategories } from '../../utils/services/categoriesAPI';
import { ELEMENT_POSITION_TOOLBAR_ID } from '../../artboard/Toolbar/elementPositionToolbar';
import CustomTooltip from '../../components/CustomTooltip/CustomTooltip';
import { TEMPLATE_NAME_CHAR_LIMIT } from '../../utils/constants/appConstants';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { getIndexedDBData, putIndexedDBData } from '../../utils/indexedDBUtil';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../context/complexVersionContext';
import { ApprovalProcess } from './ApprovalProcess/ApprovalProcess';
import { truncateText } from '../../utils/helper';
import SendIdEmail from '../InteractiveDocumentDesign/SendIdEmail';
import { useEmbedding } from '../../context/embedContext';
import { DocStudioNavLogo } from '../../components/SidebarMenuIcons';

const NAMESPACES = [
  'TEMPLATES_DESIGN_AREA_TOOLBAR',
  'COMPLEX_DESIGN_TOOLBAR',
  'COMPLEX_DOCUMENT_ENVIRONMENT',
  'COMPLETE_TRANSACTION'
];

const ComplexDocumentName = ({ editable }) => {
  const { t } = useTranslation(NAMESPACES);
  const containerRef = useRef(null);
  const pageWrapperTop = useRef(null);
  const { embedState } = useEmbedding();
  const customSelector = useCallback(
    store => ({
      resourceName: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME],
      resourceId: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID],
      communicationId: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.COMMUNICATION_ID],
      channel: store[COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_CHANNEL]
    }),
    [],
  );
  const [docInfo, store] = useDocumentContext(customSelector, `ComplexDocumentName`);
  // const [versionView, toggleVersionView] = useState(false);
  const [blur, setBlur] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [confirmFeatureToggle, setConfirmFeatureToggle] = useState(false);
  const [{ undoStack, redoStack }] = useComplexDocumentHistory(store => ({
    undoStack: store.undoStack,
    redoStack: store.redoStack,
  }));

  const templateLockedSelector = useCallback(store => store[CONTEXT_KEYS.TEMPLATE_LOCKED], []);
  const [isTemplateLocked] = useArtboardContext(templateLockedSelector, `ComplexDocumentName`);

  const lockedUserIdSelector = useCallback(store => store[CONTEXT_KEYS.LOCKED_USER_ID], []);
  const [lockedUserID] = useArtboardContext(lockedUserIdSelector, `ComplexDocumentName`);

  const currentBlockSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_BLOCK], []);
  const [currentBlock] = useArtboardContext(currentBlockSelector, `ComplexDocumentName`);

  const currentDesignSelector = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [currentDesign, updateArtboard] = useArtboardContext(
    currentDesignSelector,
    `ComplexDocumentName`,
  );

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const rulerSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_RULER], []);
  const [rulerEnabled] = useArtboardContext(rulerSelector, `ComplexDocumentName`);

  const accessibilityPreviewSelector = useCallback(store => store[CONTEXT_KEYS.ENABLED_ACCESSIBILITY_PREVIEW], []);
  const [accessibilityPreviewEnabled] = useArtboardContext(accessibilityPreviewSelector, `ComplexDocumentName`);

  const previewSelector = useCallback(store => store[CONTEXT_KEYS.IS_PREVIEW], []);
  const [isPreview] = useArtboardContext(previewSelector, `ComplexDocumentName`);

  const generatingPDFSelector = useCallback(store => store[CONTEXT_KEYS.GENERATING_PDF], []);
  const [generatingPDF] = useArtboardContext(generatingPDFSelector, `ComplexDocumentName`);

  const previewDownloadSelector = useCallback(store => store[CONTEXT_KEYS.PREVIEW_AND_DOWNLOAD], []);
  const [isPreviewDownload] = useArtboardContext(previewDownloadSelector, `ComplexDocumentName`);

  const previewCurrentPageSelector = useCallback(store => store[CONTEXT_KEYS.PREVIEW_PAGE_NUMBER], []);
  const [previewCurrentPageNumber] = useArtboardContext(previewCurrentPageSelector, `ComplexDocumentName`);

  const interactiveDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_INTERACTIVE_DESIGN], []);
  const [isInteractiveDesign] = useArtboardContext(interactiveDesignSelector, `ComplexDocumentName`);

  const isOpenDesignSelector = useCallback(store => store[CONTEXT_KEYS.IS_OPEN_DESIGN], []);
  const [isOpenDesign] = useArtboardContext(isOpenDesignSelector, `ComplexDocumentName`);

  const interactiveDocumentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.INTERACTIVE_DOCUMENT_ID], []);
  const [interactiveDocumentId] = useDocumentContext(interactiveDocumentIdSelector, `ComplexDocumentName`);

  const versionSettingsVisibleSelector = useCallback(
    store => store[VERSION_CONTEXT_KEYS.VERSION_SETTINGS_VISIBLE],
    [],
  );
  const [versionSettingsVisible] = useVersionContext(versionSettingsVisibleSelector);

  const currentVersionSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_VERSION], []);
  const [currentVersion] = useVersionContext(currentVersionSelector, `ComplexDocumentName`);

  const visibleTagSelector = useCallback(
      store => store[VERSION_CONTEXT_KEYS.CURRENT_VISIBLE_TAG],
      [],
    );
  const [currentTag] = useVersionContext(visibleTagSelector, `ComplexDocumentName`);

  const artboardVersionSelector = useCallback(store => store[CONTEXT_KEYS.ARTBOARD_VERSION], []);
  const [artboardVersion] = useArtboardContext(artboardVersionSelector, 'ComplexDocumentName');

  const readonlySelector = useCallback(store => store[CONTEXT_KEYS.READONLY_TEMPLATE], []);
  const [readOnly] = useArtboardContext(readonlySelector, 'ComplexDocumentName');

  const [sendEmailVisible, setSendEmailVisible] = useState(false);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [fetchingCategories, setFetchingCategories] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const undoNameChange = useCallback(() => {
    setBlur(false);
    const blurTimeOut = setTimeout(() => {
      setBlur(true);
      clearTimeout(blurTimeOut);
    }, 100);
  }, []);

  const changeTemplateName = async input => {
    // Validations: The template name must not be empty, and it should differ from the previous one
    // If the input value is empty, restore the previous template name
    const name = input.trim();
    if(name.length > TEMPLATE_NAME_CHAR_LIMIT) {
      message.error(t(COMPLEX_DESIGN_TOOLBAR.MAX_CHAR_LIMIT_EXCEEDED_ERROR.key, COMPLEX_DESIGN_TOOLBAR.MAX_CHAR_LIMIT_EXCEEDED_ERROR.value, { charLimitLength: TEMPLATE_NAME_CHAR_LIMIT }));
      return;
    }
    if (name.length < 1) {
      undoNameChange();
      message.warn(
        t(
          COMPLEX_DESIGN_TOOLBAR.TEMPLATE_NAME_EMPTY_WARNING_MESSAGE.key,
          COMPLEX_DESIGN_TOOLBAR.TEMPLATE_NAME_EMPTY_WARNING_MESSAGE.value,
        ),
      );
      return;
    }

    if (name !== docInfo.resourceName) {
      let payload = { name };
      if (currentDesign === 'document') {
        payload.documentId = docInfo.resourceId;
      } else if (currentDesign === 'flow') {
        payload.flowId = docInfo.resourceId;
      } else {
        payload.blockId = docInfo.resourceId;
      }
      let response;
      if (currentDesign === 'document') {
        if(isOpenDesign) {
          const currentDocument = await getIndexedDBData('openDocument');
          currentDocument.name = name;
          putIndexedDBData('openDocument', currentDocument);
          return;
        } else {
          response = await updateCommunicationName(docInfo.communicationId, name);
        }
      } else {
        response = await updateResourceNames(payload, currentDesign !== 'document');
      }
      if (response?.statusCode === 3201 || response?.statusCode === 1804 || response?.statusCode === 1927) {
        store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.DOCUMENT_NAME]: name });
      } else {
        if (response?.statusCode === 1941 || response?.statusCode === 2616 ||  response?.statusCode === 3200) {
          const error = response?.errors?.[0];

          if (error?.code === 5601) {
            const unsupportedChars = extractUnsupportedCharacters(error.description);

            message.error(
              t(
                COMPLEX_DESIGN_TOOLBAR.DOCUMENT_NAME_UNSUPPORTED_CHARS.key,
                COMPLEX_DESIGN_TOOLBAR.DOCUMENT_NAME_UNSUPPORTED_CHARS.value,
                { char: unsupportedChars }
              )
            );
          }
          undoNameChange();
        } else {
          if(response?.statusCode === 3200 && response?.errors?.some((error => error?.code === 3003))) {
            message.error(t(
              COMPLEX_DESIGN_TOOLBAR.COMMUNICATION_NAME_EXIST_ERROR.key,
              COMPLEX_DESIGN_TOOLBAR.COMMUNICATION_NAME_EXIST_ERROR.value
            ));
          } else {
            message.error(
            t(
              COMPLEX_DESIGN_TOOLBAR.UPDATE_TEMPLATE_NAME_ERROR_MESSAGE.key,
              COMPLEX_DESIGN_TOOLBAR.UPDATE_TEMPLATE_NAME_ERROR_MESSAGE.value,
            ));
          }
        }
      }
    }
  };

  const fetchCategoriesForImport = useCallback(async () => {
    try {
      setFetchingCategories(true);
      const response = await getAllCategories(false, 'element');
      if (response?.statusCode === 507 && response?.data) {
        setCategories(response.data);
        // Auto-select if only one category
        if (response.data.length === 1) {
          setSelectedCategoryId(response.data[0].id);
        }
      } else {
        message.error('Failed to load categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('Failed to load categories');
    } finally {
      setFetchingCategories(false);
    }
  }, []);

  const importTemplate = file => {
    // Store the file and show category selection modal
    setPendingFile(file);
    if (categories.length === 0) {
      fetchCategoriesForImport().then(() => {
        setCategoryModalVisible(true);
      });
    } else {
      setCategoryModalVisible(true);
    }
    return false; // Prevent default upload
  };

  const handleCategoryConfirm = () => {
    if (!selectedCategoryId) {
      message.error('Please select a category');
      return;
    }
    setCategoryModalVisible(false);
    if (pendingFile) {
      const reader = new FileReader();
      reader.onload = (event) => handleDesignUpload(event, selectedCategoryId);
      reader.readAsArrayBuffer(pendingFile);
      setPendingFile(null);
    }
  };

  const handleDesignUpload = useCallback(async (event, categoryId) => {
    if (!categoryId) {
      message.error('Category is required for import');
      return;
    }

    let designJSON = {};
    try {
      const zipFile = unzipSync(new Uint8Array(event.target.result));
      const languageCode = store.get()[COMPLEX_DOCUMENT_CONTEXT_KEYS.LANGUAGE];
      console.log({ languageCode });
      const pseudoFile = strFromU8(zipFile[partialMatchKey(zipFile, `${languageCode}.txt`)]);
      if (pseudoFile.length === 0) {
        message.error(`Unable to import for selected language!!`);
        return;
      }
      let instructions = pseudoFile.split("\n").map((line, index) => {
        const tabs = line.includes('\t') ? line.split('\t').length - 1 : line.replaceAll('    ', '\t').split('\t').length - 1;
        return {
          tabs,
          instruction: line.replaceAll('\t', '').replaceAll('    ', '')
        };
      });
      console.log({ instructions });

      // Fetch all reusable sections
      const payload = {
        categoryId: categoryId,
      };
      const response = await fetchStandaloneBlock(payload);
      const reusableMap = response.data.reduce((acc, reusable) => {
        return Object.assign({}, acc, {
          [`${reusable.parentBlockName.toUpperCase()}`]: {
            blockId: reusable.parentBlockId,
            blockDTOList: reusable.blocks,
            style: reusable.style,
            variableContract: reusable.variableContract
          }
        })
      }, {});

      let params = {
        name: IMPORT_REUSABLE_PREFIX,
      };
      const imageResponse = await getAllImages(params);
      const imageMap = imageResponse.data.reduce((acc, image) => {
        return Object.assign({}, acc, {
          [image.name.toUpperCase()]: image.imageLink
        });
      }, {});

      const designJSON = constructDesignFromInstructions(instructions, zipFile, false, languageCode, reusableMap, imageMap, t);
      console.log({ documentDTO: designJSON, reusableMap });

      updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: true });

      const updateDocument = {
        flows: designJSON.flows.map((flow, index) => {
          delete flow.flowId;
          if (flow.header) delete flow.header.blockId;
          if (flow.footer) delete flow.footer.blockId;
          return Object.assign({}, flow, {
            blocks: flow.blocks.map(block => {
              if (!block.isStandalone) {
                delete block.blockId; // Delete blockId only if it is not a standalone
              }
              return block;
            })
          });
        }),
        style: designJSON.style
      }
      console.log({ updateDocument });
      store.replaceDocument(updateDocument.flows, updateDocument.style);
      updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false });
    } catch (err) {
      console.error("Error in handleDesignUpload :: ", err);
    }
  }, [store, updateArtboard, t]);

  const onRulerVisibilityChange = value => {
    updateArtboard({ [CONTEXT_KEYS.ENABLE_RULER]: value });
  };

  const handleReadModeChange = async e => {
    let payload = {
      resourceId: isInteractiveDesign ? interactiveDocumentId : docInfo.resourceId,
      resourceType: isInteractiveDesign ? 'interactive_design' : currentDesign,
    };
    setIsLoading(true);
    try {
      const lockStatus = await checkLockStatus(payload);
      if (lockStatus.statusCode === 741) {
        if (userDetails.id !== lockStatus.data.userId) {
          updateArtboard({
            [CONTEXT_KEYS.TEMPLATE_LOCKED]: true,
            [CONTEXT_KEYS.LOCKED_USER_ID]: lockStatus.data.userId,
          });
          return;
        }
      }
      if (!isTemplateLocked) {
        const response = await unlockResource(payload);
        if (response.statusCode === 721) {
          updateArtboard({
            [CONTEXT_KEYS.PAGE_LAYOUT_VISIBLE]: false,
            [CONTEXT_KEYS.CURRENT_BLOCK]: '',
            [CONTEXT_KEYS.SWITCH_CREATIVE_DESIGN_MENU]: false,
            [CONTEXT_KEYS.TEMPLATE_LOCKED]: true,
            [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: false,
            [CONTEXT_KEYS.IS_DRAFT_EDITABLE]: false,
          });
        } else {
          message.error(
            t(
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_READ_MODE.key,
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_READ_MODE.value,
            ),
          );
        }
      } else {
        const response = await lockResource(payload);
        if (response.statusCode === 719) {
          updateArtboard({
            [CONTEXT_KEYS.TEMPLATE_LOCKED]: false,
            [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: true,
            [CONTEXT_KEYS.IS_DRAFT_EDITABLE]: true,
          });
        } else {
          message.error(
            t(
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_EDITABLE_MODE.key,
              COMPLEX_DESIGN_TOOLBAR.ERROR_SWITCHING_EDITABLE_MODE.value,
            ),
          );
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviewDownload = () => {
    updateArtboard({ 
      [CONTEXT_KEYS.PREVIEW_AND_DOWNLOAD]: !isPreviewDownload, 
      [CONTEXT_KEYS.PREVIEW_PAGE_NUMBER]: 1,
      ...( isPreview && { [CONTEXT_KEYS.ENABLED_ACCESSIBILITY_PREVIEW]: false }),
    });
    const complexPageWrapper = document.getElementById(COMPLEX_DOCUMENT_PAGE_WRAPPER);
    if (!isPreviewDownload === false) {
      if (pageWrapperTop.current !== null) {
        complexPageWrapper.scrollTop = pageWrapperTop.current;
        pageWrapperTop.current = null;
      }
    } else {
      pageWrapperTop.current = complexPageWrapper.scrollTop;
    }
    if (isOpenDesign) {
      setTimeout(() => {
        const target = document.querySelector('[data-complete-transaction-ref]');

        if (!target) {
          console.warn('target not found');
          return;
        }

        target.dispatchEvent(
          new CustomEvent('startDownload', {
            detail: {
              isPreview: true,
              downloadPayload: false,
              format: 'json',
              isCompleteTransaction: false,
              setLoader: () => {},
            },
          }),
        );
      }, [10])
    }
  };

  const onPreviewPageNumberChange = (value) =>{
    if(value){
      updateArtboard({ [CONTEXT_KEYS.PREVIEW_PAGE_NUMBER]:value });
      const previewElement = document.getElementById('preview-portal');
      previewElement.dispatchEvent(
        new CustomEvent('scrollToPreviewPageNumber', { detail: {pageNumber:value} }),
      );
    }
  }

  const triggerTransaction = () => {
    setConfirmFeatureToggle(false);
    const target = document.querySelector('[data-complete-transaction-ref]');

    if (!target) {
      console.warn('target not found');
      return;
    }

    target.dispatchEvent(
      new CustomEvent('startDownload', {
        detail: {
          isPreview: false,
          downloadPayload: false,
          format: 'json',
          setLoader: setTransactionLoading,
        },
      }),
    );
  };

  const versionMap = {
    draft: t(COMPLEX_DESIGN_TOOLBAR.VERSION_DRAFT.key, COMPLEX_DESIGN_TOOLBAR.VERSION_DRAFT.value),
    qaready: t(
      COMPLEX_DESIGN_TOOLBAR.VERSION_TESTING.key,
      COMPLEX_DESIGN_TOOLBAR.VERSION_TESTING.value,
    ),
    sandbox: t(
      COMPLEX_DESIGN_TOOLBAR.VERSION_TESTING.key,
      COMPLEX_DESIGN_TOOLBAR.VERSION_TESTING.value,
    ),
    published: t(
      COMPLEX_DESIGN_TOOLBAR.VERSION_PUBLISHED.key,
      COMPLEX_DESIGN_TOOLBAR.VERSION_PUBLISHED.value,
    ),
    live: t(
      COMPLEX_DESIGN_TOOLBAR.VERSION_PUBLISHED.key,
      COMPLEX_DESIGN_TOOLBAR.VERSION_PUBLISHED.value,
    ),
  };

  const versionName = versionMap[currentVersion];

  return (
    <>
      <TemplateName ref={containerRef} previewEnabled={isPreview}>
        {isPreview && <div class="blur-overlay"></div>}
        <div style={embedState.hideDesignTemplateName ? { marginLeft: '20px', display: 'flex', alignItems: 'center' } : {}}>
          {isInteractiveDesign && embedState.hideDesignSidebar && (
            <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '12px', flexShrink: 0 }}>
              <DocStudioNavLogo style={{ width: '24px', height: '24px', transform: 'rotate(-35deg)' }} />
            </span>
          )}
          {!embedState.hideDesignTemplateName && <DocumentName
            disabled={!editable || isInteractiveDesign}
            templateName={docInfo.resourceName}
            changeTemplateName={changeTemplateName}
            onBlur={blur}
            isElement={false}
          />}
          {!isInteractiveDesign && versionSettingsVisible && <Icon type="branches" />}
          {versionSettingsVisible && <p className='current-version'>
            {currentTag?.tagName ? <>{truncateText(currentTag.tagName, 20)} - {versionMap[currentTag.env]}</> : versionName}
          </p>}
          {editable && (
            <>
              <Tooltip
                placement="bottom"
                title={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.UNDO_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.UNDO_TOOLTIP.value,
                )}
              >
                <ActionButton
                  id="undoBtn"
                  disabled={!editable && undoStack.length === 0}
                  onClick={store.undo}
                >
                  <UndoIcon color={undoStack.length === 0 ? '#6a7580' : '#fff'} />
                </ActionButton>
              </Tooltip>
              <Tooltip
                placement="bottom"
                title={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.REDO_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.REDO_TOOLTIP.value,
                )}
              >
                <ActionButton
                  id="redoBtn"
                  disabled={!editable && redoStack.length === 0}
                  onClick={store.redo}
                >
                  <RedoIcon color={redoStack.length === 0 ? '#6a7580' : '#fff'} />
                </ActionButton>
              </Tooltip>
              <Tooltip
                placement="bottom"
                title={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.AUTO_SAVE_CHANGES_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.AUTO_SAVE_CHANGES_TOOLTIP.value,
                )}
              >
                <AutoSaveButton editable={editable} noInternetTooltip={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.NO_INTERNET_TOOLTIP.key}`,
                  COMPLEX_DESIGN_TOOLBAR.NO_INTERNET_TOOLTIP.value,
                )} />
              </Tooltip>
            </>
          )}
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'start',
            width: '100%',
          }}
        >
          <div
            id="document-editor-toolbar"
            style={{
              margin: 5,
              height: 30,
              position: 'relative',
              color: 'black',
              display: 'flex',
              alignItems: 'center',
            }}
          />
          <div id={ELEMENT_POSITION_TOOLBAR_ID} />
        </div>
        <PreviewEndContainer isPreview={isPreview}>
          <ApprovalProcess />
          {ENTERPRISE_ENV && !currentBlock && !isPreview && !accessibilityPreviewEnabled && currentDesign === 'document' && !isInteractiveDesign && !versionSettingsVisible && (
            <Upload accept=".zip" action={importTemplate} showUploadList={false}>
              <Tooltip
                title={'Import Design script'}
              >
                <ImportButton ghost type="primary" icon="download" className="topMenu" style={{ margin: 10 }} />
              </Tooltip>
            </Upload>
          )}
          {isPreview && !accessibilityPreviewEnabled && (
            <PreviewPageNumberInput className={isPreview && 'previewPageNumber'}>
              <Typography.Text>{t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.PREVIEW_PAGE_NO.key}`,
                  COMPLEX_DESIGN_TOOLBAR.PREVIEW_PAGE_NO.value,
                )}</Typography.Text>
              <InputNumber
                placeholder={t(
                  `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.PREVIEW_PAGE_NUMNER.key}`,
                  COMPLEX_DESIGN_TOOLBAR.PREVIEW_PAGE_NUMNER.value,
                )}
                defaultValue={previewCurrentPageNumber || 1}
                value={previewCurrentPageNumber}
                onChange={onPreviewPageNumberChange}
                type='number'
              />
            </PreviewPageNumberInput>
          )}
          {/* These tooltips will be displayed when the component initially mounts, 
            remaining visible for a certain amount of time, or they will close when the user clicks anywhere */}
          <CustomTooltip
            title={t(
              `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.DOWNLOAD_PREVIEW_TOOLTIP_TEXT.key}`,
              COMPLEX_DESIGN_TOOLBAR.DOWNLOAD_PREVIEW_TOOLTIP_TEXT.value,
            )}
            placement={'left'}
            purpleColor
          >
            {!currentBlock && (
              <>
                {isPreviewDownload && isInteractiveDesign && docInfo?.channel === 'email' ?
                  (<DownloadButton
                    className={`transaction-btn ${transactionLoading ? 'loading' : ''}`}
                    onClick={() => editable && setSendEmailVisible(true)}
                    interactiveEditable={editable && isInteractiveDesign}
                  >
                    {t(
                      `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.SEND_LETTER_LABEL.key}`,
                      COMPLEX_DESIGN_TOOLBAR.SEND_LETTER_LABEL.value,
                    )}
                  </DownloadButton>)
                  : (isPreviewDownload && isInteractiveDesign) ?
                    (
                      <DownloadButton
                        className={`transaction-btn ${transactionLoading ? 'loading' : ''}`}
                        onClick={() => editable && setConfirmFeatureToggle(true)}
                        interactiveEditable={editable && isInteractiveDesign}
                      >
                        {t(
                          `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.COMPLETE_TRANSACTION_LABEL.key}`,
                          COMPLEX_DESIGN_TOOLBAR.COMPLETE_TRANSACTION_LABEL.value,
                        )}
                        {transactionLoading && (
                          <Spin
                            style={{ paddingLeft: 10 }}
                            indicator={<Icon type="loading" style={{ color: 'black' }} />}
                          ></Spin>
                        )}
                      </DownloadButton>
                    ) : null}
                {embedState.hidePreviewButton ? null : <Button ghost onClick={handlePreviewDownload} loading={generatingPDF} className={isPreview && 'exit_preview'}>
                  {
                isPreview
                  ? isInteractiveDesign
                    ? t(
                        `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.EXIT_PREVIEW_AND_DOWNLOAD_BUTTON.key}`,
                        COMPLEX_DESIGN_TOOLBAR.EXIT_PREVIEW_AND_DOWNLOAD_BUTTON.value
                      )
                    : checkUserAccessRoles(userDetails, ["DESIGNER", "PUBLISHER"])
                      ? t(
                          `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.EXIT_PREVIEW_AND_DOWNLOAD_BUTTON.key}`,
                          COMPLEX_DESIGN_TOOLBAR.EXIT_PREVIEW_AND_DOWNLOAD_BUTTON.value
                        )
                      : t(
                          `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.EXIT_PREVIEW_BUTTON.key}`,
                          COMPLEX_DESIGN_TOOLBAR.EXIT_PREVIEW_BUTTON.value
                        )
                : generatingPDF
                  ? "Downloading Documents..."
                  : isInteractiveDesign
                    ? t(
                        `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.PREVIEW_AND_DOWNLOAD_BUTTON.key}`,
                        COMPLEX_DESIGN_TOOLBAR.PREVIEW_AND_DOWNLOAD_BUTTON.value
                      )
                    : checkUserAccessRoles(userDetails, ["DESIGNER", "PUBLISHER"])
                      ? t(
                          `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.PREVIEW_AND_DOWNLOAD_BUTTON.key}`,
                          COMPLEX_DESIGN_TOOLBAR.PREVIEW_AND_DOWNLOAD_BUTTON.value
                        )
                      : t(
                          `COMPLEX_DESIGN_TOOLBAR:${COMPLEX_DESIGN_TOOLBAR.PREVIEW_BUTTON.key}`,
                          COMPLEX_DESIGN_TOOLBAR.PREVIEW_BUTTON.value
                        )
              }
              </Button>
              }</>
            )}
          </CustomTooltip>
          {/* Tooltip for Zoho Support widget */}
          {document.getElementById('pdsSupportWidget') && <CustomTooltip
            title={t(
              COMPLEX_DESIGN_TOOLBAR.SUPPORT_TOOLTIP_TEXT.key,
              COMPLEX_DESIGN_TOOLBAR.SUPPORT_TOOLTIP_TEXT.value,
            )}
            widget
          />}
        </PreviewEndContainer>
        <EndContainer currentBlock={currentBlock} isPreview={isPreview} style={embedState.hideDesignTemplateName ? { paddingRight: '20px' } : undefined}>          
          <div className={`small-switch switch1 ${embedState.hideDesignTemplateName ? 'inline-layout' : ''}`}>
            <Switch checked={rulerEnabled} onChange={onRulerVisibilityChange} size="small" />
            <span className="switch-name">
              {t(COMPLEX_DESIGN_TOOLBAR.RULER_LABEL.key, COMPLEX_DESIGN_TOOLBAR.RULER_LABEL.value)}
            </span>
          </div>
          {!isOpenDesign && !readOnly && !embedState.hideReadModeButton &&
            <div className="small-switch switch2">
              <Switch
                checked={isTemplateLocked}
                disabled={lockedUserID !== userDetails.id}
                onChange={e => handleReadModeChange(e)}
                loading={isLoading}
                size="small"
              />
              <span className="switch-name">
                {t(
                  COMPLEX_DESIGN_TOOLBAR.READ_MODE_LABEL.key,
                  COMPLEX_DESIGN_TOOLBAR.READ_MODE_LABEL.value,
                )}
              </span>
            </div>
          }
        </EndContainer>
      </TemplateName>
      {versionSettingsVisible && artboardVersion === 'draft' && !readOnly ? (
        <ComplexDocumentVersion documentId={docInfo.resourceId} />
      ) : null}
      <ConfirmationModal
        showImage={false}
        visibility={confirmFeatureToggle}
        onCancel={() => setConfirmFeatureToggle(false)}
        onConfirm={() => triggerTransaction()}
        confirmButtonText={t(
          COMPLETE_TRANSACTION.CONFIRM_BUTTON.key,
          COMPLETE_TRANSACTION.CONFIRM_BUTTON.value,
        )}
        confirmationText={t(
          COMPLETE_TRANSACTION.CONFIRM_MODAL_DESCRIPTION.key,
          COMPLETE_TRANSACTION.CONFIRM_MODAL_DESCRIPTION.value,
        )}
      />
      <SendIdEmail
        docId={interactiveDocumentId}
        visible={sendEmailVisible}
        onCancel={() => setSendEmailVisible(false)}
        onConfirm={() => setSendEmailVisible(false)}
      />
      <Modal
        title="Select Category"
        visible={categoryModalVisible}
        onOk={handleCategoryConfirm}
        onCancel={() => {
          setCategoryModalVisible(false);
          setPendingFile(null);
          setSelectedCategoryId(null);
        }}
        okText="Import"
        cancelText="Cancel"
        okButtonProps={{ disabled: !selectedCategoryId || fetchingCategories }}
      >
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
            Category: <span style={{ color: 'red' }}>*</span>
          </label>
          <Select
            style={{ width: '100%' }}
            placeholder="Select a category"
            value={selectedCategoryId}
            onChange={setSelectedCategoryId}
            loading={fetchingCategories}
            disabled={fetchingCategories}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {categories.map((category) => (
              <Select.Option key={category.id} value={category.id}>
                {category.name}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>
    </>
  );
};

export default withTranslation(NAMESPACES)(ComplexDocumentName);
